import { ControlledClickPopover } from "src/components/ui-components/ClickPopover";
import { GetStartedButton } from "./GetStartedButton";
import { Session } from "src/utils/users/types";
import { useGetCreatorOnboardingChecklist } from "src/gql/creatorOnboardingChecklist/useGetCreatorOnboardingChecklist";
import { CheckList } from "./CheckList";
import { TOTAL_STEPS, checkListItems } from "./content";
import { useContext, useEffect, useRef, useState } from "react";
import { Confetti } from "../ui-components/Confetti/Confetti";
import { Content } from "shared/OnboardingQuestions/Content";
import styles from "./CheckList.module.css";
import AmplitudeService from "src/services/amplitude";
import { SessionContext } from "../session/session";

export function CheckListPopover({ session }: { session: Session }) {
  const { refresh: refreshSession } = useContext(SessionContext);
  const checklist = useGetCreatorOnboardingChecklist(session);
  const [isOpen, setIsOpen] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  const skippedOnboardingQsUntil =
    session?.user?.private_info?.onboarding_qs_skipped_until;
  const [showOnboardingQuestions, setShowOnboardingQuestions] = useState(
    skippedOnboardingQsUntil === null
  );

  const querySaved = checklist?.querySavedAt;
  const visualizationCreated = checklist?.visualizationCreatedAt;
  const visualizationShared = checklist?.visualizationSharedAt;

  const creatorChecklist = {
    ["account"]: { completed: true, withDetails: false },
    ["query"]: {
      completed: Boolean(querySaved !== null),
      withDetails: Boolean(querySaved === null),
    },
    ["visualization"]: {
      completed: Boolean(visualizationCreated !== null),
      withDetails: Boolean(
        querySaved !== null && visualizationCreated === null
      ),
    },
    ["share"]: {
      completed: Boolean(visualizationShared !== null),
      withDetails: Boolean(
        querySaved !== null &&
          visualizationCreated !== null &&
          visualizationShared === null
      ),
    },
  };

  const stepsCompleted = Object.values(creatorChecklist).filter(
    ({ completed }) => Boolean(completed)
  ).length;
  const percentage = (stepsCompleted / TOTAL_STEPS) * 100;

  const initialStepsRef = useRef(stepsCompleted);

  useEffect(() => {
    if (initialStepsRef.current !== stepsCompleted && stepsCompleted === 4) {
      setIsOpen(true);
      setShowConfetti(true);
    }

    const timer = setTimeout(() => {
      setShowConfetti(false);
    }, 5000); // Animation duration
    return () => clearTimeout(timer);
  }, [stepsCompleted]);

  if (!checklist) {
    return null;
  }

  const onDismiss = () => {
    setShowOnboardingQuestions(false);
    setIsOpen(true);
  };

  if (showOnboardingQuestions) {
    return (
      <>
        <div className={styles.overlay} />
        <ControlledClickPopover
          isOpen={true}
          disableEscKey
          setIsOpen={setIsOpen}
          position="below-align-right"
          content={() => (
            <Content
              session={session}
              userId={session.user!.id}
              onQsSubmitted={() => {
                onDismiss();
                refreshSession();
              }}
            />
          )}
          closeOnClickOutside={false}
        >
          <div className={styles.highlightedElement}>
            <GetStartedButton percentage={0} />
          </div>
        </ControlledClickPopover>
      </>
    );
  }

  return (
    <li>
      <ControlledClickPopover
        isOpen={isOpen}
        setIsOpen={(newIsOpen) => {
          if (newIsOpen === true && isOpen === false) {
            AmplitudeService.trackEvent("Get Started Button Clicked");
          }
          setIsOpen(newIsOpen);
        }}
        closeOnClickOutside
        position="below-align-right"
        content={({ close }) => (
          <div>
            <CheckList
              checklist={checkListItems}
              checklistItemsState={creatorChecklist}
              stepsCompleted={stepsCompleted}
              onHide={close}
            />
            {showConfetti && <Confetti />}
          </div>
        )}
      >
        <div>
          <GetStartedButton percentage={percentage} />
        </div>
      </ControlledClickPopover>
    </li>
  );
}
